import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Badge } from "../../ui/badge";
import { Router, Network, CheckCircle2 } from "lucide-react";
import { Device } from "../interfaces/EndpointInterfaces";
import ConfirmationDialog from "./ConfirmationDialog";

interface GatewayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: Device | null;
  onSave: (deviceId: string, selectedSubnets: string[], label: string) => void;
  onCancel: () => void;

}

export default function GatewayDialog({
  open,
  onOpenChange,
  device,
  onSave,
  onCancel
}: GatewayDialogProps) {

  const availableSubnets =
    device?.IPV4Addresses?.filter((ip) => !ip.startsWith("100.64")) || [];


  const [selectedSubnets, setSelectedSubnets] = useState<string[]>(device?.gatewayIpAddresses || []);
  const [showUnconfigConfirmation, setShowUnconfigConfirmation] = useState<boolean>(false);
  const handleSubnetToggle = (subnet: string, checked: boolean) => {
    setSelectedSubnets(prev =>
      checked
        ? [...prev, subnet]
        : prev.filter(s => s !== subnet)
    );
  };

  const handleSubmit = () => {
    if (device) {
      if (unconfigCondition) {
        setShowUnconfigConfirmation(true);
      } else {
        onSave(device.id, selectedSubnets, "Configure gateway");
        onOpenChange(false);
      }

    }
  };

  const handleCancel = () => {
    setSelectedSubnets([]);
    onOpenChange(false);
    onCancel();
  };

  if (!device) return null;


  let unconfigCondition = device.isGateway && selectedSubnets.length === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} >
        <DialogContent className="sm:max-w-[520px]" data-testid="gateway-dialog">
          <DialogHeader className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg dark:bg-blue-900/20">
                <Router className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Gateway Configuration
                </DialogTitle>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Configure network routing for device: <span className="font-medium text-slate-800 dark:text-slate-200">{device?.name}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <Network className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Available Network Subnets</span>
              <Badge variant="outline" className="ml-auto text-xs">
                {selectedSubnets.length} selected
              </Badge>
            </div>

            <div className="overflow-hidden border rounded-lg shadow-sm border-slate-200 dark:border-slate-700">
              <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700 border-slate-200 dark:border-slate-600">
                <div className="col-span-2">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Sl No</span>
                </div>
                <div className="col-span-6">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Network CIDR</span>
                </div>
                <div className="col-span-4">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Gateway Route</span>
                </div>
              </div>

              {availableSubnets.map((subnet, index) => {
                const isSelected = selectedSubnets.includes(subnet);
                return (
                  <div
                    key={subnet}
                    className={`grid grid-cols-12 gap-3 px-4 py-3 border-b last:border-b-0 border-slate-100 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                  >
                    <div className="flex items-center col-span-2">
                      <Badge variant="secondary" className="flex items-center justify-center w-8 h-6 text-xs">
                        {index + 1}
                      </Badge>
                    </div>
                    <div className="flex items-center col-span-6">
                      <code className="px-2 py-1 font-mono text-sm rounded bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200">
                        {subnet}
                      </code>
                    </div>
                    <div className="flex items-center col-span-4 gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleSubnetToggle(subnet, checked as boolean)}
                        data-testid={`checkbox-subnet-${index}`}
                        className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      {isSelected && (
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-xs font-medium">Active</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-3 mt-3 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Network className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">Gateway Configuration</h4>
                  <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                    Selected subnets will route network traffic through this device. Ensure the device has appropriate network permissions and firewall configurations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="outline"
              onClick={handleCancel}
              data-testid="button-cancel"
              className="min-w-20"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!device.isGateway && selectedSubnets.length === 0}
              className={`text-white ${unconfigCondition ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}  min-w-24`}
              data-testid="button-submit"
            >
              {unconfigCondition ? "Unconfigure Gateway" : "Configure Gateway"}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={showUnconfigConfirmation}
        onOpenChange={setShowUnconfigConfirmation}
        title="Unconfigure gateway"
        description={`Are you sure, you want to remove this computer as gateway? `}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={() => {
          onSave(device.id, selectedSubnets, "Unconfigure gateway");
          onOpenChange(false);
        }}

      />
    </>
  );
}